import Image from "next/image"
import { X } from "lucide-react"
import {
  CUSTOMIZATION_PRICING,
  CUSTOMIZATION_LABELS,
  CUSTOMIZATION_LIMITS,
  CUSTOMIZATION_PATCHES,
} from "@constants/product-page"
import { convertToLocale } from "@lib/util/money"
import { useDisplayPrice } from "@modules/common/components/display-price"

type ProductCustomizationProps = {
  customName: string
  customNumber: string
  selectedPatch: string
  currencyCode: string
  onNameChange: (name: string) => void
  onNumberChange: (number: string) => void
  onPatchChange: (patchId: string) => void
}

export default function ProductCustomization({
  customName,
  customNumber,
  selectedPatch,
  currencyCode,
  onNameChange,
  onNumberChange,
  onPatchChange,
}: ProductCustomizationProps) {
  /**
   * These add-on prices are hardcoded GBP figures in src/constants, so they must
   * be converted like any other amount — otherwise a EUR-viewing customer sees a
   * euro symbol against a pound number (#166532).
   */
  const displayPrice = useDisplayPrice()
  const formatPrice = (price: number) => displayPrice(price, currencyCode)

  return (
    <div className="space-y-6">
      {/* Personalisation Card */}
      <div className="border-2 border-brand-navy rounded-2xl p-5 small:p-6 space-y-5">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-baseline gap-x-2">
            <p className="text-base small:text-lg font-black text-brand-navy">
              {CUSTOMIZATION_LABELS.sectionTitle}
            </p>
            <span className="text-base small:text-lg text-brand-navy font-normal">
              {CUSTOMIZATION_LABELS.sectionOptional}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {CUSTOMIZATION_LABELS.sectionDescription}
          </p>
        </div>

        {/* Player name & number form (single mode — no tabs) */}
        <div className="space-y-4">
          <p className="text-sm small:text-base font-black text-brand-navy">
            {CUSTOMIZATION_LABELS.nameAndNumberTitle} ( +
            {formatPrice(CUSTOMIZATION_PRICING.nameAndNumber)} )
          </p>

          {/* Name Input */}
          <div className="relative">
            <input
              type="text"
              value={customName}
              onChange={(e) => {
                const value = e.target.value.slice(
                  0,
                  CUSTOMIZATION_LIMITS.nameMaxLength
                )
                onNameChange(value)
              }}
              placeholder={CUSTOMIZATION_LABELS.customNamePlaceholder}
              maxLength={CUSTOMIZATION_LIMITS.nameMaxLength}
              aria-label="Custom name"
              className="w-full px-5 py-3 pr-28 text-sm font-medium text-brand-navy border-2 border-gray-200 rounded-full outline-none placeholder:text-brand-navy placeholder:font-medium focus:border-brand-navy transition-colors"
            />
            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {CUSTOMIZATION_LABELS.customNameHint}
            </span>
          </div>

          {/* Number Input */}
          <div className="relative">
            <input
              type="text"
              value={customNumber}
              onChange={(e) => {
                const value = e.target.value
                  .slice(0, CUSTOMIZATION_LIMITS.numberMaxLength)
                  .replace(/[^0-9]/g, "")
                onNumberChange(value)
              }}
              placeholder={CUSTOMIZATION_LABELS.customNumberPlaceholder}
              maxLength={CUSTOMIZATION_LIMITS.numberMaxLength}
              aria-label="Custom number"
              className="w-full px-5 py-3 pr-28 text-sm font-medium text-brand-navy border-2 border-gray-200 rounded-full outline-none placeholder:text-brand-navy placeholder:font-medium focus:border-brand-navy transition-colors"
            />
            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {CUSTOMIZATION_LABELS.customNumberHint}
            </span>
          </div>
        </div>
      </div>

      {/* Select Patches */}
      <div className="space-y-4">
        <p className="text-base small:text-lg font-black text-brand-navy">
          Select patches
        </p>
        <div className="flex items-center gap-6">
          {CUSTOMIZATION_PATCHES.map((patch) => {
            const isSelected = selectedPatch === patch.id
            return (
              <button
                key={patch.id}
                type="button"
                onClick={() => onPatchChange(patch.id)}
                aria-pressed={isSelected}
                className="flex items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded-lg"
              >
                <div
                  className={`w-[64px] h-[64px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                    isSelected
                      ? "border-brand-navy bg-white"
                      : "border-gray-300 bg-gray-50 hover:border-gray-400"
                  }`}
                >
                  {patch.image ? (
                    <Image
                      src={patch.image}
                      alt={patch.name}
                      width={40}
                      height={40}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <X
                      className="w-7 h-7 text-gray-300"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="text-left">
                  <p
                    className={`text-sm font-bold ${
                      isSelected ? "text-brand-navy" : "text-gray-700"
                    }`}
                  >
                    {patch.name}
                  </p>
                  <p
                    className={`text-sm ${
                      isSelected ? "text-brand-navy" : "text-gray-500"
                    }`}
                  >
                    {patch.price === 0
                      ? formatPrice(0)
                      : `(+${formatPrice(patch.price)})`}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
